import React, { useState, useEffect } from 'react';

const ToastNotification = ({ message, type = 'info', duration = 3000, details = null, title = null, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchOffset, setTouchOffset] = useState(0);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, 300); // Wait for exit animation
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  // Touch handlers for swipe-to-dismiss on mobile
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (touchStart === null) return;
    const currentTouch = e.touches[0].clientY;
    const diff = touchStart - currentTouch;
    setTouchOffset(diff);
  };

  const handleTouchEnd = () => {
    // If swiped up more than 50px, dismiss
    if (touchOffset > 50) {
      handleClose();
    }
    setTouchStart(null);
    setTouchOffset(0);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`toast-notification ${type} ${isExiting ? 'exiting' : 'entering'}`}
      style={{ transform: touchOffset > 0 ? `translateY(-${touchOffset}px)` : 'translateY(0)' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <button className="toast-close-btn" onClick={handleClose} aria-label="Close notification">
        ✕
      </button>
      <div className="toast-content">
        {title && (
          <h3>
            <span className="toast-icon">
              {type === 'long' && '📈'}
              {type === 'short' && '📉'}
              {type === 'success' && '✅'}
              {type === 'error' && '❌'}
              {type === 'warning' && '⚠️'}
              {type === 'market_open' && '🔔'}
              {type === 'market_close' && '🔕'}
              {type === 'bull_run' && '🚀'}
              {type === 'market_crash' && '💥'}
              {type === 'volatility' && '📊'}
              {type === 'stock_split' && '✂️'}
              {type === 'merger' && '🤝'}
              {type === 'earnings' && '💰'}
              {type === 'user_event' && '👤'}
              {type === 'ticker_positive' && '📈'}
              {type === 'ticker_negative' && '📉'}
              {type === 'market_positive' && '🟢'}
              {type === 'market_negative' && '🔴'}
              {type === 'info' && '📢'}
            </span>
            {title}
          </h3>
        )}
        {message && (
          <p className="toast-message">{String(message)}</p>
        )}
        {details && (
          <p className="toast-details">{String(details)}</p>
        )}
      </div>
    </div>
  );
};

// Toast Manager Component
export const ToastManager = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    // Listen for custom toast events
    const handleToast = (event) => {
      const { message, type, duration, details, title } = event.detail;
      console.log('[ToastManager] Received toast event:', { message, type, title });
      const id = Date.now() + Math.random();
      setToasts(prev => {
        const next = [...prev, { id, message, type, duration, details, title }];
        console.log('[ToastManager] Total toasts now:', next.length);
        return next;
      });
    };

    console.log('[ToastManager] Mounted and listening for showToast events');
    window.addEventListener('showToast', handleToast);
    return () => {
      console.log('[ToastManager] Unmounted');
      window.removeEventListener('showToast', handleToast);
    };
  }, []);

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <ToastNotification
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration || 3000}
          details={toast.details}
          title={toast.title}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

// Helper function to trigger toast notifications
export const showToast = (message, type = 'info', duration = 3000, details = null, title = null) => {
  // Ensure message is a string
  const messageStr = typeof message === 'object' ? JSON.stringify(message) : String(message || '');
  const detailsStr = details ? (typeof details === 'object' ? JSON.stringify(details) : String(details)) : null;
  const titleStr = title ? (typeof title === 'object' ? JSON.stringify(title) : String(title)) : null;

  console.log('[showToast] Called with:', { message: messageStr, type, duration, details: detailsStr, title: titleStr });

  window.dispatchEvent(new CustomEvent('showToast', {
    detail: {
      message: messageStr,
      type,
      duration,
      details: detailsStr,
      title: titleStr
    }
  }));
};

export default ToastNotification;